import React, { useState } from 'react';
import { X } from 'lucide-react';

const MaintenanceModal = ({ assetId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    type: 'maintenance',
    description: '',
    cost: '',
    technician: '',
    parts_replaced: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert parts_replaced from comma-separated string to array
      const partsArray = formData.parts_replaced
        ? formData.parts_replaced.split(',').map(p => p.trim()).filter(p => p)
        : [];

      const submitData = {
        type: formData.type,
        description: formData.description,
        cost: parseFloat(formData.cost) || 0,
        technician: formData.technician,
        parts_replaced: partsArray
      };

      // Call API
      const { assetsAPI } = await import('../services/api');
      await assetsAPI.addMaintenance(assetId, submitData);

      alert('✅ Maintenance/Service berhasil dicatat!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error recording maintenance:', error);
      alert('❌ Gagal mencatat maintenance: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-card p-6 rounded-xl border border-border/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-foreground">Catat Maintenance/Service</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Jenis <span className="text-destructive">*</span>
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
              required
            >
              <option value="maintenance">🔧 Maintenance (Perawatan Rutin)</option>
              <option value="service">⚠️ Service (Perbaikan)</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Deskripsi <span className="text-destructive">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors resize-none"
              rows="3"
              placeholder="Deskripsi lengkap maintenance/service yang dilakukan..."
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cost */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">
                Biaya (Rp)
              </label>
              <input
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                placeholder="0"
                min="0"
              />
            </div>

            {/* Technician */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">
                Teknisi
              </label>
              <input
                type="text"
                value={formData.technician}
                onChange={(e) => setFormData({ ...formData, technician: e.target.value })}
                className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                placeholder="Nama teknisi"
              />
            </div>
          </div>

          {/* Parts Replaced */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Part yang Diganti
            </label>
            <input
              type="text"
              value={formData.parts_replaced}
              onChange={(e) => setFormData({ ...formData, parts_replaced: e.target.value })}
              className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
              placeholder="RAM, SSD, Keyboard (pisahkan dengan koma)"
            />
            <p className="text-xs text-muted-foreground mt-1">
              💡 Pisahkan dengan koma untuk multiple parts
            </p>
          </div>

          {/* Info */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-sm text-blue-600">
              💡 <strong>Tanggal maintenance terakhir</strong> akan otomatis diupdate setelah menyimpan record ini.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
              disabled={loading}
            >
              Batal
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Menyimpan...' : 'Catat Maintenance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaintenanceModal;
